import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from 'assets/search-icon.svg';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import { FormikDatePicker } from 'components/shared/Form/DatePicker';
import SelectField from 'components/shared/Form/SelectField';
import { useFormikContext } from 'formik';
import { FieldConfig } from 'model/deals.model';
import zeplinColor from 'theme/Color';

interface GenericFormSectionProps {
  fields: FieldConfig[];
  dropdowns?: Record<string, string[] | { [key: string]: string }[]>;
  autocompleteMap?: {
    accountId: any[];
    businessId: any[];
    ownerId: any[];
  };
}

const GenericFormSection = ({
  fields,
  dropdowns = {},
  autocompleteMap,
}: GenericFormSectionProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const { values, handleChange, handleBlur } = useFormikContext<any>();

  const renderField = (f: FieldConfig) => {
    const value = values[f.name] ?? '';

    switch (f.type) {
      case 'date':
        return <FormikDatePicker name={f.name} label={f.label} required={f.required ?? false} />;

      case 'checkbox':
        return (
          <FormControlLabel
            label={f.label}
            control={
              <Checkbox
                name={f.name}
                checked={Boolean(value)}
                onChange={(e) =>
                  handleChange({ target: { name: f.name, value: e.target.checked } } as any)
                }
                onBlur={handleBlur}
              />
            }
          />
        );

      case 'select':
        let options = dropdowns[f.name] || f.options || [];
        if (f.name === 'dealTypeId') options = dropdowns.dealType;

        return (
          <SelectField
            field={f}
            value={value}
            options={options}
            onChange={handleChange}
            onBlur={handleBlur}
            isXs={isXs}
          />
        );

      case 'autocomplete':
        if (!autocompleteMap) return;
        const selectedValue =
          autocompleteMap[f.name as keyof typeof autocompleteMap]?.find(
            (opt) => opt.id === value,
          ) || null;

        return (
          <Autocomplete
            size={isXs ? 'small' : 'medium'}
            value={selectedValue}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  fontSize: isXs ? 14 : 16,
                  '& .MuiFormLabel-asterisk': {
                    color: zeplinColor.Error,
                  },
                }}
                required={f.required ?? false}
                label={<Typography variant='responsiveBody2'>{f.label}</Typography>}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <img src={SearchIcon} width={20} height={20} alt='search' />
                      </InputAdornment>
                    ),
                    style: { paddingRight: 14 },
                  },
                }}
              />
            )}
            onChange={(_, newValue) => {
              handleChange({
                target: {
                  name: f.name,
                  value: newValue?.id,
                },
              });
            }}
            onBlur={handleBlur}
            options={autocompleteMap[f.name as keyof typeof autocompleteMap]}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option) => option.id === value}
          />
        );

      case 'text':
      default:
        return (
          <CustomTextField
            required={f.required}
            name={f.name}
            label={f.label}
            type={f.type === 'number' ? 'number' : 'text'}
            text={value}
            onChange={handleChange}
            onBlur={() => handleBlur(f.name)}
            multiline={f.multiline}
            form={true}
            style={{ width: '100%' }}
          />
        );
    }
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, width: '100%' }}>
      {fields.map((f, i) => (
        <Box key={`${f.name}-${i}`} sx={{ gridColumn: { xs: 'span 4' } }}>
          {renderField(f)}
        </Box>
      ))}
    </Box>
  );
};

export default GenericFormSection;
